import {
  ActivityType,
  activityLabel,
  EscalationOutcomeLabels,
  IContactActivityResponseItem,
} from "shared";
import { When } from "react-if";
import {
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline";

import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { WrappedLink } from "../WrappedLink";

import { CallTranscript } from "./CallTranscript";
import { NoteTimestamp } from "./NoteTimestamp";

const activityTypeIcons: Record<ActivityType, typeof PhoneIcon> = {
  [ActivityType.PHONE_CALL]: PhoneIcon,
  [ActivityType.TEXT_MESSAGE]: ChatBubbleLeftRightIcon,
  [ActivityType.EXTERNAL_EMAIL]: EnvelopeIcon,
};

interface ActivityItemProps {
  activity: IContactActivityResponseItem;
}

export function ActivityItem({ activity }: ActivityItemProps) {
  const name = `${activity.userFirstName || ``} ${activity.userLastName || ``}`.trim();
  const Icon = activityTypeIcons[activity.type] || PhoneIcon;

  return (
    <div
      id={ `activity-${activity.id}` }
      className={ `border border-gray-100 rounded-md p-3` }
    >
      <div className={ `flex justify-between` }>
        <div className={ `flex items-center` }>
          <Icon className={ `h-5 w-5 text-slate-500 mr-1` } />
          <Paragraph
            variant={ `secondary` }
          >
            <When condition={ !!name }>
              <Paragraph
                as={ `span` }
                className={ `font-semibold` }
              >
                { name }
              </Paragraph>
            </When>
            <span>
              { `${name ? ` logged a ` : `Logged a `}${activityLabel(activity.type, activity.callOutcome)}` }
            </span>

            <When condition={ !!(activity.invoiceId && activity.invoiceNumber) }>
              <WrappedLink to={ `/invoices/${activity.invoiceId}` }>
                { ` on invoice ` }
                <Paragraph
                  as={ `span` }
                  variant={ `link` }
                >
                  <span className={ `font-semibold` }>
                    { activity.invoiceNumber }
                  </span>
                </Paragraph>
              </WrappedLink>
            </When>
          </Paragraph>
        </div>

        <NoteTimestamp date={ activity.activityTime } />
      </div>

      <When condition={ !!activity.noteContent }>
        <Paragraph
          variant={ `note` }
          className={ `mt-2 whitespace-pre-wrap` }
        >
          { activity.noteContent }
        </Paragraph>
      </When>

      { activity.contactCallId && (
        <CallTranscript
          organisationId={ activity.organisationId }
          contactId={ activity.contactId }
          contactCallId={ activity.contactCallId }
          callerName={ activity.userFirstName }
        />
      ) }

      { activity.noteOutcome && (
        <Paragraph
          variant={ `help` }
          size={ `xs` }
          className={ `mt-2` }
        >
          { `Outcome: ${EscalationOutcomeLabels[activity.noteOutcome] || activity.noteOutcome}` }
        </Paragraph>
      ) }
    </div>
  );
}
